import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../../components/ui/table';
import { toast } from 'sonner';
import { CalendarDays, Loader2, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import PricingReportCard from './PricingReportCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const fmtZAR = (v) => (v ?? 0).toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' });

// Daily Investor Pricing — the T+1 workflow: pick a date (defaults to
// yesterday), review each investor's loan-position total + cash position as
// of that date, then explicitly publish. Doubles as the general "view any
// past day" tool — you can pick ANY date, past or present, purely to look;
// publishing is a separate, explicit action.
const AdminInvestorPricingPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [pricingDate, setPricingDate] = useState(yesterday());
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const fetchPreview = useCallback(async (dateStr) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/investor-pricing/as-of`, {
        headers, params: { date: dateStr },
      });
      setPreview(r.data);
    } catch (err) {
      toast.error('Failed to load pricing preview');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPreview(pricingDate); }, [pricingDate, fetchPreview]);

  const handlePublish = async (force = false) => {
    setPublishing(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/investor-pricing/publish`,
        { date: pricingDate, force }, { headers });
      toast.success(`Published pricing for ${pricingDate}`);
      await fetchPreview(pricingDate);
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error(err.response.data?.detail || 'Already published for this date', {
          action: {
            label: 'Republish anyway',
            onClick: () => handlePublish(true),
          },
        });
      } else {
        toast.error('Failed to publish pricing');
      }
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="investor-pricing-page">
      <div>
        <h1 className="text-2xl font-bold">Daily Investor Pricing</h1>
        <p className="text-gray-500 text-sm mt-1">
          Loan position (as of the selected date) plus cash position (as of the selected date), per investor.
          Pick any date to preview it — nothing is saved until you explicitly publish.
        </p>
      </div>

      <PricingReportCard headers={headers} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><CalendarDays className="h-5 w-5" />Select Date</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          <div>
            <Label htmlFor="pricing-date">Pricing date</Label>
            <input
              id="pricing-date"
              type="date"
              className="block mt-1 border rounded-md px-3 py-2 text-sm"
              value={pricingDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setPricingDate(e.target.value)}
              data-testid="pricing-date-input"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchPreview(pricingDate)} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          {preview?.already_published && (
            <Badge className="bg-green-100 text-green-800">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Published {preview.published_meta?.published_at?.slice(0, 16).replace('T', ' ')}
              {preview.published_meta?.published_by ? ` by ${preview.published_meta.published_by}` : ''}
            </Badge>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Preview — {pricingDate}</CardTitle>
            <CardDescription>Recomputed live from LoanBook/Risk Participation + the investor cash ledger. Read-only until published.</CardDescription>
          </div>
          <Button
            onClick={() => handlePublish(false)}
            disabled={publishing || loading || !preview}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="publish-pricing-btn"
          >
            {publishing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Publishing...</> : 'Publish'}
          </Button>
        </CardHeader>
        <CardContent>
          {preview?.already_published && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              This date is already published. Publishing again keeps the old version in history — it doesn't overwrite it silently.
            </div>
          )}
          {loading && <p className="text-sm text-gray-500">Loading...</p>}
          {!loading && preview && (
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Investors</p><p className="text-xl font-bold">{preview.investors.length}</p></div>
                <div className="p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Grand Total</p><p className="text-xl font-bold">{fmtZAR(preview.grand_total)}</p></div>
                <div className="p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">As Of</p><p className="text-xl font-bold">{preview.as_of}</p></div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Investor</TableHead>
                      <TableHead>RP Code</TableHead>
                      <TableHead className="text-right">Loans</TableHead>
                      <TableHead className="text-right">Loan Position</TableHead>
                      <TableHead className="text-right">Cash</TableHead>
                      <TableHead className="text-right">Total Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.investors.map((inv) => (
                      <TableRow key={inv.investor_id || inv.rp_code}>
                        <TableCell>{inv.name}</TableCell>
                        <TableCell>{inv.rp_code}</TableCell>
                        <TableCell className="text-right">{inv.loan_count}</TableCell>
                        <TableCell className="text-right">{fmtZAR(inv.loan_position_total)}</TableCell>
                        <TableCell className="text-right">{fmtZAR(inv.cash_balance)}</TableCell>
                        <TableCell className="text-right font-medium">{fmtZAR(inv.total_price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminInvestorPricingPage;
